import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus, ChevronDown, Mail, Power } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Spinner } from "@/components/common/Common";
import { ActivityFeed } from "@/components/common/ActivityFeed";
import { DocumentsPanel } from "@/components/common/DocumentsPanel";
import { DaysRemaining, StatusBadge } from "@/components/common/StatusBadge";
import { ContractEmailDialog, textToHtml } from "@/components/email/ContractEmailDialog";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDate, formatError } from "@/lib/api";
import { DEACTIVATION_REASONS, STATUS_LABELS } from "@/lib/constants";
import { DeactivateDialog, ExtendDialog } from "./ContractDialogs";
import { LafhaTab, OverviewTab } from "./OverviewTab";
import { BankLeaseTab, RatesTab } from "./RatesBankTabs";
import { OnboardingTab } from "./OnboardingTab";

function Header({ c, canWrite, onStatus, open }) {
  return (
    <div className="sticky top-[101px] z-30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 bg-white/90 backdrop-blur-md border-b border-slate-200 mb-6" data-testid="contract-header">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/contractors/${c.contractor_id}`} className="text-xl sm:text-2xl font-bold text-slate-900 hover:text-blue-700 truncate" data-testid="contract-contractor-name">{c.contractor_name}</Link>
            <span className="font-mono text-xs bg-slate-100 border border-slate-200 rounded px-2 py-0.5" data-testid="contract-employment-id">{c.employment_id}</span>
            {canWrite ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="inline-flex items-center gap-1" data-testid="status-menu-trigger"><StatusBadge status={c.status} testId="contract-status-badge" /><ChevronDown className="h-3 w-3 text-slate-400" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {Object.entries(STATUS_LABELS).filter(([k]) => k !== "inactive" && k !== c.status).map(([k, v]) => (
                    <DropdownMenuItem key={k} onClick={() => onStatus(k)} data-testid={`status-option-${k}`}>{v}</DropdownMenuItem>
                  ))}
                  {c.status !== "inactive" && <><DropdownMenuSeparator /><DropdownMenuItem className="text-red-600" onClick={() => open("deactivate")} data-testid="status-option-deactivate">Deactivate…</DropdownMenuItem></>}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : <StatusBadge status={c.status} testId="contract-status-badge" />}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {c.recruitment_company_name || "No recruitment company"} · {fmtDate(c.start_date)} – {fmtDate(c.end_date)} · <DaysRemaining days={c.days_remaining} status={c.status} /> days left · {c.assigned_to_name || "Unassigned"}
          </p>
          {c.status === "inactive" && <p className="text-xs text-slate-500 mt-1" data-testid="deactivation-info">Inactive since {fmtDate(c.deactivated_on)} — {DEACTIVATION_REASONS[c.deactivation_reason] || "no reason"}</p>}
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => open("email")} data-testid="contract-email-button"><Mail className="h-4 w-4 mr-1" />Email</Button>
            <Button variant="outline" size="sm" onClick={() => open("extend")} data-testid="contract-extend-button"><CalendarPlus className="h-4 w-4 mr-1" />Extend</Button>
            {c.status !== "inactive" && <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => open("deactivate")} data-testid="contract-deactivate-button"><Power className="h-4 w-4 mr-1" />Deactivate</Button>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ContractPage() {
  const { id } = useParams();
  const { canWrite } = useAuth();
  const [c, setC] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [draft, setDraft] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const load = useCallback(() => api.get(`/contracts/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(formatError(e))), [id]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => { const { data } = await api.patch(`/contracts/${id}`, patch); setC(data); setReloadKey((k) => k + 1); };
  const onStatus = async (status) => {
    try { const { data } = await api.post(`/contracts/${id}/status`, { status }); setC(data); toast.success(`Status changed to ${STATUS_LABELS[status]}`); } catch (e) { toast.error(formatError(e)); }
  };
  const openDialog = (name) => {
    if (name === "email") setDraft({ to: [c.contractor_email].filter(Boolean), cc: [], subject: `${c.employment_id} — `, html: "" });
    setDialog(name);
  };

  if (!c) return <div className="flex justify-center p-12"><Spinner /></div>;
  const ro = !canWrite;
  return (
    <div>
      <Header c={c} canWrite={canWrite} onStatus={onStatus} open={openDialog} />
      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-slate-200 flex-wrap h-auto mb-5" data-testid="contract-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="rates" data-testid="tab-rates">Rates</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding">Onboarding</TabsTrigger>
          {c.lafha_eligible && <TabsTrigger value="lafha" data-testid="tab-lafha">LAFHA</TabsTrigger>}
          <TabsTrigger value="bank" data-testid="tab-bank">Bank & lease</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails">Emails & notes</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><OverviewTab c={c} save={save} readOnly={ro} /></TabsContent>
        <TabsContent value="rates"><RatesTab c={c} reloadKey={reloadKey} /></TabsContent>
        <TabsContent value="onboarding"><OnboardingTab c={c} onStatusChange={onStatus} reloadKey={reloadKey} onChase={(d) => { setDraft({ ...d, html: textToHtml(d.body) }); setDialog("email"); }} /></TabsContent>
        {c.lafha_eligible && <TabsContent value="lafha"><LafhaTab c={c} save={save} readOnly={ro} /></TabsContent>}
        <TabsContent value="bank"><BankLeaseTab c={c} onChanged={load} /></TabsContent>
        <TabsContent value="emails"><ActivityFeed url={`/contracts/${id}/activity`} contractId={id} reloadKey={reloadKey} /></TabsContent>
        <TabsContent value="documents"><DocumentsPanel contractId={id} /></TabsContent>
      </Tabs>
      {dialog === "deactivate" && <DeactivateDialog contract={c} onClose={() => setDialog(null)} onDone={(d) => { setC(d); setDialog(null); setReloadKey((k) => k + 1); }} />}
      {dialog === "extend" && <ExtendDialog contract={c} onClose={() => setDialog(null)} onDone={(d) => { setC(d); setReloadKey((k) => k + 1); setDraft({ template_code: "extension" }); setDialog("offer-email"); }} />}
      {dialog === "offer-email" && (
        <div className="fixed bottom-6 right-6 z-50 bg-white border border-slate-200 shadow-lg rounded-lg p-4 max-w-sm animate-fade-up" data-testid="extension-email-offer">
          <p className="text-sm text-slate-800 font-medium">Send the extension email?</p>
          <p className="text-xs text-slate-500 mt-1">You can review it before anything is sent.</p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={() => setDialog("email")} data-testid="extension-email-yes">Review email</Button>
            <Button size="sm" variant="ghost" onClick={() => setDialog(null)} data-testid="extension-email-no">Not now</Button>
          </div>
        </div>
      )}
      {dialog === "email" && <ContractEmailDialog contract={c} initial={draft} onClose={() => setDialog(null)} onSent={() => setReloadKey((k) => k + 1)} />}
    </div>
  );
}
